import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FavoriteIcon from "@mui/icons-material/Favorite";
import {
  formatViews,
  getRandomDefaultImg,
  slugify,
  normalizeImageUrl,
} from "../../utility/general.utils";
import {
  optimizeImageUrl,
  getResponsiveSrcSet,
  getResponsiveSizes,
  getOptimalImageWidth,
} from "../../utility/image.utils";
import { HeadingGradient, TextGradient } from "../Gradient";
import DefaultImageDisplay from "../DefaultImageDisplay";
import type { Story } from "../../types/models";

interface HomeCarouselSectionProps {
  title: string;
  stories: Story[];
  showNumbers?: boolean;
}

const HomeCarouselSection: React.FC<HomeCarouselSectionProps> = ({
  title,
  stories,
  showNumbers = false,
}) => {
  const nextRef = useRef(null);
  const prevRef = useRef(null);
  const swiperRef = useRef<any>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(480));
  const navigate = useNavigate();

  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  if (!stories || stories.length === 0) {
    return null;
  }

  const showNavigation = stories.length > 5;

  return (
    <Box
      sx={{
        position: "relative",
        mb: { xs: 4, sm: 5, md: 8 },
      }}
    >
      {/* Section Title */}
      <Box
        sx={{
          mb: { xs: 2, sm: 2.5, md: 3 },
          px: { xs: 1.5, sm: 2, md: 3 }, // Match Container padding exactly
        }}
      >
        <HeadingGradient
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
            mb: 0.5,
          }}
        >
          {title}
        </HeadingGradient>
      </Box>

      <Box sx={{ position: "relative", overflow: "visible" }}>
        {/* Navigation Buttons - Left and Right - Only show if more than 5 stories */}
        {showNavigation && (
          <>
            {/* Previous Button - Left side - Only show if not at beginning */}
            {!isBeginning && (
              <IconButton
                ref={prevRef}
                onClick={() => {
                  if (swiperRef.current) {
                    swiperRef.current.slidePrev();
                  }
                }}
                sx={{
                  position: "absolute",
                  top: "40%",
                  left: { xs: 4, sm: 6, md: 8 },
                  transform: "translateY(-50%)",
                  zIndex: 10,
                  width: { xs: 32, sm: 36, md: 40 },
                  height: { xs: 32, sm: 36, md: 40 },
                  borderRadius: "50%",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(255, 255, 255, 0.7)",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)"
                    }`,
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? "#fff"
                      : theme.palette.text.primary,
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                      : "0 4px 12px rgba(0, 0, 0, 0.15)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.05)",
                    transform: "translateY(-50%) scale(1.1)",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 6px 16px rgba(0, 0, 0, 0.4)"
                        : "0 6px 16px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                <ArrowForwardIosIcon
                  fontSize="small"
                  sx={{
                    transform: "rotate(180deg)",
                  }}
                />
              </IconButton>
            )}

            {/* Next Button - Right side - Only show if not at end */}
            {!isEnd && (
              <IconButton
                ref={nextRef}
                onClick={() => {
                  if (swiperRef.current) {
                    swiperRef.current.slideNext();
                  }
                }}
                sx={{
                  position: "absolute",
                  top: "40%",
                  right: { xs: 4, sm: 6, md: 8 },
                  transform: "translateY(-50%)",
                  zIndex: 10,
                  width: { xs: 32, sm: 36, md: 40 },
                  height: { xs: 32, sm: 36, md: 40 },
                  borderRadius: "50%",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(255, 255, 255, 0.7)",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.1)"
                    }`,
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? "#fff"
                      : theme.palette.text.primary,
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                      : "0 4px 12px rgba(0, 0, 0, 0.15)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.05)",
                    transform: "translateY(-50%) scale(1.1)",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 6px 16px rgba(0, 0, 0, 0.4)"
                        : "0 6px 16px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}

        {/* Swiper Carousel */}
        <Box
          sx={{
            overflow: "hidden",
            px: { xs: 1.5, sm: 2, md: 3 }, // Match title padding exactly
            "& .swiper": {
              margin: 0,
              padding: 0,
              overflow: "hidden",
            },
            "& .swiper-wrapper": {
              margin: 0,
              padding: 0,
              display: "flex",
            },
            "& .swiper-slide": {
              flexShrink: 0,
            },
          }}
        >
          <Swiper
            modules={showNavigation ? [Navigation] : []}
            spaceBetween={18}
            slidesOffsetBefore={0} // No offset needed - container has padding
            slidesOffsetAfter={12} // Keep end padding for visual balance
            slidesPerView="auto"
            slidesPerGroup={1}
            loop={false}
            grabCursor={true}
            speed={600}
            resistance={true}
            resistanceRatio={0.85}
            watchOverflow={true}
            navigation={
              showNavigation
                ? { nextEl: nextRef.current, prevEl: prevRef.current }
                : false
            }
            onInit={(swiper) => {
              swiperRef.current = swiper;
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
              if (
                showNavigation &&
                swiper.params.navigation &&
                typeof swiper.params.navigation === "object"
              ) {
                swiper.params.navigation.nextEl = nextRef.current;
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.navigation.init();
                swiper.navigation.update();
              }
            }}
            onSlideChange={(swiper) => {
              // Wait for transition to complete before updating state
              setTimeout(() => {
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
              }, 0);
            }}
            onTransitionEnd={(swiper) => {
              // Update state after transition completes
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
              // Force update navigation state
              if (swiper.navigation) {
                swiper.navigation.update();
              }
            }}
            onReachBeginning={(swiper) => {
              setIsBeginning(true);
            }}
            onReachEnd={(swiper) => {
              setIsEnd(true);
            }}
            onTouchEnd={(swiper) => {
              // Update state after touch/mouse drag
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            breakpoints={{
              0: {
                slidesPerView: "auto",
                slidesPerGroup: 1,
                spaceBetween: 18,
                slidesOffsetBefore: 0, // Padding handled by container
                slidesOffsetAfter: 12,
              },
              600: {
                slidesPerView: "auto",
                slidesPerGroup: 1,
                spaceBetween: 18,
                slidesOffsetBefore: 0, // Padding handled by container
                slidesOffsetAfter: 16,
              },
              900: {
                slidesPerView: "auto",
                slidesPerGroup: 1,
                spaceBetween: 18,
                slidesOffsetBefore: 0, // Padding handled by container
                slidesOffsetAfter: 24,
              },
            }}
            className="home-carousel-swiper"
            style={{
              paddingRight: "0px",
              paddingLeft: "0px",
              margin: 0,
            }}
          >
            {stories.map((story, index) => {
              const imageUrl = story?.coverPhoto || story?.imgUrl;
              const isDefaultImage = story?.isDefaultImage || false;
              const isOneShot = story?.type === "ONE_SHOT";
              const number = showNumbers ? index + 1 : null;
              // First 3 images in first carousel (non-numbered) should be LCP candidates
              const isLCPCandidate =
                index < 3 && !showNumbers && title === "Top picks for you";

              return (
                <SwiperSlide
                  key={story.id}
                  style={{
                    width: isMobile ? 120 : isSmallScreen ? 150 : 170, // Exact card width (spaceBetween handles spacing)
                    height: "auto",
                  }}
                >
                  <Paper
                    elevation={0}
                    component={Link}
                    to={`/story/${story.id}/${slugify(story.title)}`}
                    sx={{
                      width: "100%",
                      maxWidth: { xs: 120, sm: 150, md: 170 },
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: { xs: 1.5, md: 2 },
                      backgroundColor: "transparent",
                      overflow: "hidden",
                      textDecoration: "none",
                      color: "inherit",
                      "&:hover .cover-image": {
                        transform: "scale(1.08)",
                      },
                    }}
                  >
                    {/* Cover Image Container - Wattpad Style */}
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: "3/4",
                        minHeight: { xs: 160, sm: 200, md: 227 }, // Adjusted for 3:4 ratio (120*4/3=160, 150*4/3=200, 170*4/3=227)
                        maxHeight: { xs: 160, sm: 200, md: 227 },
                        overflow: "hidden",
                        borderRadius: { xs: 1.5, md: 2 },
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.03)",
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.15)",
                      }}
                    >
                      {/* Cover Image - Optimized for performance */}
                      {isDefaultImage && story?.title ? (
                        <Box
                          className="cover-image"
                          sx={{
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                            transition: "transform 0.4s ease",
                          }}
                        >
                          <DefaultImageDisplay
                            title={story.title}
                            width={600}
                            height={800}
                            textAlignment="left"
                            sx={{
                              width: "100%",
                              height: "100%",
                              "& canvas": {
                                transition: "transform 0.4s ease",
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <img
                          className="cover-image"
                          src={optimizeImageUrl(
                            normalizeImageUrl(imageUrl) ||
                              getRandomDefaultImg(),
                            getOptimalImageWidth(170)
                          )}
                          srcSet={getResponsiveSrcSet(
                            normalizeImageUrl(imageUrl) ||
                              getRandomDefaultImg(),
                            [120, 240, 340, 400]
                          )}
                          sizes={getResponsiveSizes("170px")}
                          alt={story.title}
                          width={isMobile ? 120 : isSmallScreen ? 150 : 170}
                          height={isMobile ? 160 : isSmallScreen ? 200 : 227}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            transition: "transform 0.4s ease",
                          }}
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            if (
                              target.src !== getRandomDefaultImg() &&
                              imageUrl
                            ) {
                              target.src = getRandomDefaultImg();
                            }
                          }}
                          loading={isLCPCandidate ? "eager" : "lazy"}
                          decoding="async"
                        />
                      )}

                      {/* Number Badge (for Top 10 sections) - Simple Style */}
                      {number && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            left: 6,
                            zIndex: 2,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            backdropFilter: "blur(8px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 800,
                              fontSize: "0.75rem",
                              color: "#fff",
                            }}
                          >
                            {number}
                          </Typography>
                        </Box>
                      )}

                      {/* Story Type Badge - Minimal Top Right */}
                      {story?.type && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            zIndex: 2,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.3,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            backdropFilter: "blur(8px)",
                          }}
                        >
                          {story.type === "ONE_SHOT" ? (
                            <FlashOnIcon
                              sx={{
                                fontSize: 10,
                                color: "#fbbf24",
                              }}
                            />
                          ) : (
                            <LibraryBooksIcon
                              sx={{
                                fontSize: 10,
                                color: "#a5b4fc",
                              }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.55rem",
                              color: "#fff",
                            }}
                          >
                            {story.type === "ONE_SHOT" ? "ONE-SHOT" : "SERIES"}
                          </Typography>
                        </Box>
                      )}

                      {/* Completed Badge - Bottom Left */}
                      {story.status === "completed" && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 6,
                            left: 6,
                            zIndex: 2,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.3,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            border: "1px solid rgba(34, 197, 94, 0.5)",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                          }}
                        >
                          <CheckCircleIcon
                            sx={{
                              fontSize: 10,
                              color: "#22c55e",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.65rem",
                              color: "#22c55e",
                              letterSpacing: 0.3,
                            }}
                          >
                            COMPLETED
                          </Typography>
                        </Box>
                      )}

                      {/* Likes and Views Count - Bottom Right - Vertically Stacked */}
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 6,
                          right: 6,
                          zIndex: 2,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                          alignItems: "flex-end",
                        }}
                      >
                        {/* Views */}
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            backdropFilter: "blur(8px)",
                          }}
                        >
                          <VisibilityIcon
                            sx={{
                              fontSize: { xs: 10, sm: 11, md: 12 },
                              color: "rgba(255, 255, 255, 0.9)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              fontSize: {
                                xs: "0.65rem",
                                sm: "0.7rem",
                                md: "0.75rem",
                              },
                              color: "#fff",
                            }}
                          >
                            {formatViews(story?.views || 0)}
                          </Typography>
                        </Box>

                        {/* Likes */}
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1.25,
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            backdropFilter: "blur(8px)",
                          }}
                        >
                          <FavoriteIcon
                            sx={{
                              fontSize: { xs: 12, sm: 13, md: 14 },
                              color: "#e53935",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              fontSize: {
                                xs: "0.55rem",
                                sm: "0.6rem",
                                md: "0.65rem",
                              },
                              color: "#fff",
                            }}
                          >
                            {formatViews(story?.totalChapterLikes || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Minimal Content Below Cover - Wattpad Style */}
                    <Box sx={{ pt: 1, pb: 0.5 }}>
                      {/* Title */}
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.8125rem",
                            md: "0.875rem",
                          },
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          overflow: "hidden",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          wordBreak: "break-word",
                          color: "text.primary",
                          mb: { xs: 0.25, md: 0.5 },
                        }}
                      >
                        {story.title}
                      </Typography>

                      {/* Author - Simple */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: {
                              xs: "0.65rem",
                              sm: "0.7rem",
                              md: "0.75rem",
                            },
                            fontWeight: 500,
                            color: "text.secondary",
                            opacity: 0.7,
                          }}
                        >
                          By
                        </Typography>
                        {story.userId ? (
                          <TextGradient
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              navigate(`/user/${story.userId}`);
                            }}
                            sx={{
                              fontSize: {
                                xs: "0.65rem",
                                sm: "0.7rem",
                                md: "0.75rem",
                              },
                              fontWeight: 500,
                              opacity: 0.8,
                              cursor: "pointer",
                              textDecoration: "none",
                              "&:hover": {
                                opacity: 1,
                              },
                            }}
                          >
                            {story.author || "Unknown"}
                          </TextGradient>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: {
                                xs: "0.65rem",
                                sm: "0.7rem",
                                md: "0.75rem",
                              },
                              fontWeight: 500,
                              color: "text.secondary",
                              opacity: 0.8,
                            }}
                          >
                            {story.author || "Unknown"}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </Box>
      </Box>
    </Box>
  );
};

export default HomeCarouselSection;
